import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import CustomizeIcon from '@atlaskit/icon/core/customize';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, xcss } from '@atlaskit/primitives';

import {
	PrimaryProductKeys,
	type ProductKeys,
	useProductConfigs,
} from '../../../../../common/constants/products';
import { PRODUCTS_WITH_INLINE_FILTERS } from '../../../../../common/constants/quick-find';
import { FilterButton } from '../../../../../common/ui/quick-find/filter-button';
import { usePrimaryProduct, useSelectedProduct } from '../../../../../controllers/store';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { ContributorFilter, SpaceFilter } from '../../../filters/product-filters';

import { i18n } from './messages';
import { useUnselectProductFilter } from './utils';

const oldInlineProductFilterStyles = xcss({
	display: 'flex',
	paddingInlineStart: 'space.100',
	gap: 'space.100',
	width: '100%',
	minWidth: '0', // Prevents the filter buttons from overflowing from its container and allow it to shrink as much as possible
});

const newInlineProductFilterStyles = xcss({
	display: 'flex',
	gap: 'space.100',
	minWidth: '0', // Prevents the filter buttons from overflowing from its container and allow it to shrink as much as possible
});

type InlineProductFiltersProps = {
	onAllFiltersClickCallback?: (params: {
		filterByPrimaryProduct?: boolean;
		filterDropdownOpen?: boolean;
		event: React.MouseEvent;
	}) => void;
};

export const InlineProductFilters = ({ onAllFiltersClickCallback }: InlineProductFiltersProps) => {
	const { formatMessage } = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const [selectedProduct] = useSelectedProduct();
	const productConfigs = useProductConfigs();

	useUnselectProductFilter();

	const oldInlineProductFilters = (
		<Box xcss={oldInlineProductFilterStyles} testId="qf-product-filters">
			{/* TODO: Dynamically populate based on selected product. Hardcoded for Team25 */}
			<SpaceFilter />
			<FilterButton
				onClick={(event) => {
					onAllFiltersClickCallback?.({
						...(fg('rovo_filters_discoverability_2') && {
							filterDropdownOpen: true,
						}),
						event,
					});
				}}
				iconAfter={
					<LinkExternalIcon
						label={formatMessage(i18n.externalLinkIconLabel)}
						color="currentColor"
						size="small"
					/>
				}
			>
				{formatMessage(i18n.moreButtonLabel)}
			</FilterButton>
		</Box>
	);

	/**
	 * Defines inline product filters to show for each primary product. Given that
	 * the filters we decide to show are determined by product and design, there
	 * isn't a need to make this dynamic yet.
	 */
	const newInlineProductFilters = useMemo(() => {
		const primaryProductDisplayName = productConfigs[primaryProduct as ProductKeys].displayName;
		const moreFiltersButton = (
			<FilterButton
				onClick={(event) => {
					onAllFiltersClickCallback?.({
						...(fg('rovo_filters_discoverability_2') && {
							filterDropdownOpen: true,
						}),
						filterByPrimaryProduct: true,
						event,
					});
				}}
				iconAfter={<CustomizeIcon label={formatMessage(i18n.filterByIcon)} />}
				noBorder={true}
				tooltip={formatMessage(i18n.moreButtonTooltip, { product: primaryProductDisplayName })}
				testId="quick-find-inline-product-filters-more-filters-button"
			/>
		);

		if (primaryProduct === PrimaryProductKeys.Confluence) {
			return (
				<Box xcss={newInlineProductFilterStyles} testId="qf-product-filters">
					<SpaceFilter />
					{fg('rovo_filters_discoverability_2') && <ContributorFilter />}
					{moreFiltersButton}
				</Box>
			);
		} else {
			return null;
		}
	}, [formatMessage, onAllFiltersClickCallback, primaryProduct, productConfigs]);

	if (PRODUCTS_WITH_INLINE_FILTERS.includes(primaryProduct) && fg('rovo_filters_discoverability')) {
		return newInlineProductFilters;
	}

	if (selectedProduct) {
		return oldInlineProductFilters;
	}

	return null;
};
